/**
 * This is a wrapper around the localStorage API. Data are stored
 * in the browser and survive between page reload and browser session.
 * Use this class to store non-sensitive information in the frontend.
 */
export default class LocalStoreService {

  /**
   * @prop {object} localItemKeys Object with all the local storage keys used in this application
   */
  public static readonly localItemKeys = {
    /** @prop {string} proUsername The username of a Pro user */
    proUsername: 'username',
    /** @prop {string} screeningUnit The screening unit (e.g. fr, vs) of the logged in user */
    screeningUnit: 'su',
    /** @prop {string} dossierId The dossier/participant ID */
    dossierId: 'dossier',
    /** @prop {string} policies The list of policies */
    policies: 'POLICIES',
    /** @prop {string} appVersionTrys The number of try to refresh the app */
    appVersionTrys: 'appVersionTrys',
  };

  /**
   * Get an item from local storage. If the item doesn't exists we return an empty string by default.
   * An empty string doesn't necessarily mean that the item doesn't exist : use the itemExists() for this purpose.
   * @param key The item key to get
   * @returns {string} The item at key in the local storage.
   */
  public static getItem(key: string): string {
    const val = localStorage.getItem(this.getKey(key));
    if (val === null) {
      return '';
    }
    return val;
  }

  /**
   * Set (add or update) an item in local storage
   * @param key The item key to set
   * @param value The item value to set
   */
  public static setItem(key: string, value: string): void {
    localStorage.setItem(this.getKey(key), value);
  }

  /**
   * Delete/remove an item from local storage
   * @param key The item key to delete/remove
   */
  public static deleteItem(key: string): void {
    localStorage.removeItem(this.getKey(key));
  }

  /**
   * Check if a key exists in the local storage
   * @param key The item key to check
   * @returns {boolean} True if an item exists in the local sotrage, false otherwise.
   */
  public static itemExists(key: string): boolean {
    const val = localStorage.getItem(this.getKey(key));
    if (val === null) {
      return false;
    }
    return true;
  }

  /**
   * Clears all the data stored in local storage for the current application.
   */
  public static clearAll(): void {
    for (const [prop, keyString] of Object.entries(this.localItemKeys)) {
      this.deleteItem(keyString);
    }
  }

  /** The prefix used on every key the app stores in Local Storage */
  private static readonly prefix = 'mcsis:equest';

  /**
   * Get the key with prefix.
   * @param {string} key
   */
  private static getKey(key: string): string {
    return `${this.prefix}:${key}`.trim();
  }
}
